/*
 * Cockpit backend client — the tiered provisioning surface (P3, design §8).
 *
 * Same-origin via the console nginx proxy (/v1/cockpit/* → the cockpit app),
 * so the browser talks same-origin with no CORS, exactly like probePlaneHealth
 * (console/src/api/client.ts:224). The browser CANNOT present a client cert;
 * Tier-1/2 privileged routes are reached only behind the mTLS-terminating
 * proxy, which forwards the verified X-Operator-Cert-Thp (nginx strips any
 * inbound copy). The SPA renders that boundary honestly: privileged buttons
 * are disabled-with-explanation, never silently "clickable but failing".
 *
 * Every wrapper is GET/POST same-origin with `credentials: "omit"`; a down or
 * absent backend yields 502 through the proxy, which the Cockpit view renders
 * as its honest dormant state (never a fabricated success).
 */

/** Tier-0 Observe data source — GET /v1/cockpit/status. */
export interface CockpitStatus {
  /** Provisioning-stage flag: true once the node PKI is established. */
  pki_ready: boolean;
  /** Per-plane state keyed by plane name (e.g. {transport: "ready"}). */
  planes: Record<string, string>;
}

/** A declared member of the node (the declare-zone is operator-authored). */
export interface CockpitMember {
  id: string;
  role: string;
  services?: string[];
}

/** A cross-mesh peer the node federates with. */
export interface CockpitPeer {
  country: string;
  member: string;
}

/** Tier-0 manifest projection — GET /v1/cockpit/manifest. */
export interface CockpitManifest {
  /** The declare-zone, operator-authored and non-secret — exposed verbatim. */
  declare: {
    country: string;
    node: { member_id: string };
    members: CockpitMember[];
    consent: { channels: string[]; lawful_basis: string[]; single_use_default: boolean };
    peers: CockpitPeer[];
    planes: { enabled: string[] };
  };
  /**
   * The pin-zone is PRESENCE-ONLY: the two external trust anchors are reported
   * as booleans, never values. Both must be present for the fail-closed Deploy
   * gate to open (spec §8.2).
   */
  trust_anchors_present: { witness_genesis_pin: boolean; audit_anchor_signer_thp: boolean };
  /** The generate-zone exposes a THP only, NEVER the seed bytes (spec §14). */
  generate: { emitter_thp: string | null };
}

/** The two PUBLIC pins an operator pastes into the Tier-2 ceremony wizard. */
export interface VerifyPinsBody {
  witness_genesis_pin: string;
  audit_anchor_signer_thp: string;
}

/** POST /v1/cockpit/ceremony/verify-pins — render-don't-execute transcript. */
export interface VerifyPinsResult {
  verified: boolean;
  transcript: {
    witness_genesis_pin: string;
    audit_anchor_signer_thp: string;
    verified_at_tier: number;
  };
}

/** POST /v1/cockpit/deploy — self-audited, fail-closed deploy result. */
export interface DeployResult {
  deployed: boolean;
  /** The §38.3 audit `open` event sealed BEFORE the deploy ran. */
  audit_open_hash: string;
  deploy_rc: number;
}

/** POST /v1/cockpit/exchange/run — the step-9 consent-gated exchange intent. */
export interface ExchangeResult {
  /** "not_yet_wired" in P3 — the live four-plane hop is wired in P4. */
  status: string;
  audit_open_hash: string;
}

/** One verifier gate's honest verdict in the final seal-loop round. */
export interface SealLoopGateResult {
  name: string;
  rc: number;
  sealed: boolean;
}

/**
 * The seal-loop progress metric — DELIBERATELY split, never folded into one pct.
 *
 * Mirrors the backend route body verbatim (cli/mesh_cli/provision/cockpit/app.py
 * post_seal_loop, after the §38.3 "un-launder" rework). `sealed_gates`/`gate_count`
 * count GATES (the loop's gate set, max ~5); `gate_seal_pct` is the gate-progress
 * fraction over that set. `total_acs` is a SEPARATE denominator — the live §52 row
 * count, bound at the route via count_section52_acs(). The two count DIFFERENT
 * things, so they are not combined: an operator must NOT read gate progress as an
 * "X of Y ACs sealed" fraction. (This shape supersedes the {sealed,total,pct} the
 * task brief sketched against the pre-rework backend.)
 */
export interface SealLoopMetric {
  /** Count of GATES that sealed in the final round (numerator over the gate set). */
  sealed_gates: number;
  /** Size of the loop's gate set (the gate-progress denominator). */
  gate_count: number;
  /** sealed_gates / gate_count as a percent — gate progress, NOT a sealed-AC pct. */
  gate_seal_pct: number;
  /** The LIVE §52 AC-row count — a STANDALONE denominator, never folded with gates. */
  total_acs: number;
}

/** POST /v1/cockpit/seal-loop — the done-gate verdict + the gate-progress metric. */
export interface SealLoopRouteResult {
  /** True only when the exchange + all four verifier gates greened. */
  converged: boolean;
  sealed_gates: string[];
  /** The next gate to close, or null when converged. Honest: never faked green. */
  failed_gate: string | null;
  /** Each gate ATTEMPTED in the final round; a gate after the first red is absent. */
  gate_results: SealLoopGateResult[];
  /** Gate-progress + the live §52 count — two separate counts, never one fraction. */
  metric: SealLoopMetric;
  /** The §38.3 audit `open` event sealed BEFORE the loop ran. */
  audit_open_hash: string;
}

const BASE = "/v1/cockpit";

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Accept: "application/json" },
    mode: "same-origin",
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${BASE}${path}`);
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    mode: "same-origin",
    credentials: "omit",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${BASE}${path}`);
  return (await res.json()) as T;
}

/** Tier-0: the Observe data source (per-plane state + pki_ready stage flag). */
export function getCockpitStatus(): Promise<CockpitStatus> {
  return getJson<CockpitStatus>("/status");
}

/** Tier-0: the declare-zone projection + firewall presence chips. */
export function getCockpitManifest(): Promise<CockpitManifest> {
  return getJson<CockpitManifest>("/manifest");
}

/**
 * Tier-2: verify the two PUBLIC operator-pasted pins (render-don't-execute).
 * The SPA never holds or mints a seed; this only verifies externally-supplied
 * public commitments. Behind the mTLS boundary in a real deployment.
 */
export function verifyPins(body: VerifyPinsBody): Promise<VerifyPinsResult> {
  return postJson<VerifyPinsResult>("/ceremony/verify-pins", body);
}

/**
 * Tier-1: self-audit (audit-first) then execute the deploy. The browser cannot
 * present a client cert, so this only succeeds behind the mTLS-terminating
 * proxy; the view disables the trigger with an explanatory affordance.
 */
export function deploy(): Promise<DeployResult> {
  return postJson<DeployResult>("/deploy");
}

/** Tier-1: the step-9 consent-gated exchange intent (P3 audits + stubs). */
export function runExchange(): Promise<ExchangeResult> {
  return postJson<ExchangeResult>("/exchange/run");
}

/**
 * Tier-1: run the done-gate SEAL-LOOP and read the honest per-gate verdict +
 * gate-progress metric. Behind the mTLS boundary in a real deployment; a still-red
 * gate is reported faithfully (the loop is fail-closed — the SPA never paints a
 * red gate green). The route runs over INJECTED gates; with no live wiring a real
 * POST is 503, surfaced here as the thrown HTTP error the view renders honestly.
 */
export function runSealLoop(): Promise<SealLoopRouteResult> {
  return postJson<SealLoopRouteResult>("/seal-loop");
}
